import {Suspense, useEffect, useRef, useState} from 'react';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ERROR} from '@di-shared/const';
import {parseDateWithFormat, subDate} from '@shared/dateUtils';
import useSafeDriving from '@di-shared/features/safe-driving/useSafeDriving';
import clsx from 'clsx';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {ROUTE_PATHS} from '@di-shared/routes';
import useUbi from '@di-shared/features/ubi/useUbi';
import {useNavigate} from 'react-router';
import {CLOSE_DAYS, IRRECOVERABLE_CLOSE_DUE_DATE} from '@di-shared/features/safe-driving/kr/SafeDrivingIrrecoverableBottomSheet.kr';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {BrandType} from '@shared/cci/features/const/environment';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import type {ApiResponseGetDailyUbiResponse} from '@di-shared/api/di/__generated';
import SafeDrivingAgreeBannerKr from '@di-shared/features/safe-driving/kr/SafeDrivingAgreeBanner.kr';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ErrorBoundary} from 'react-error-boundary';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import ConsentBannerLoadingKr from '@di-shared/features/home/kr/ConsentBannerLoading.kr';
import ConsentBannerKr from '@di-shared/features/home/kr/ConsentBanner.kr';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {checkExcludeBrand} from '@di-shared/utils/whitelist';
import {PermissionTypeKr} from '@di-shared/api/di/type';
import SafeDrivingMainTripLoadingKr from '@di-shared/features/safe-driving/kr/SafeDrivingMainTripLoading.kr';
import SafeDrivingMainTripKr from '@di-shared/features/safe-driving/kr/SafeDrivingMainTrip.kr';
import SafeDrivingNightLoadingKr from '@di-shared/features/safe-driving/kr/SafeDrivingNightLoading.kr';
import SafeDrivingNightKr from '@di-shared/features/safe-driving/kr/SafeDrivingNight.kr';
import LottieSelector from '@di-shared/features/common/LottieSelector';
import SafeDrivingDailyGraphKr from '@di-shared/features/safe-driving/kr/SafeDrivingDailyGraph.kr';
import DailyLineGraphError from '@di-shared/features/common/DailyLineGraphError';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import SafeDrivingMonthlyScoreTrend from '@di-shared/features/safe-driving/kr/SafeDrivingMonthlyScoreTrend';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import {emitEventLog} from '@di-shared/features/analytics';

export interface SafeDrivingContentsKrProps {
  vin: string;
}

// 최대 90일간의 날짜 조회 => 90일치의 데이터가 보여야함 (오늘을 포함하기 때문)
export const MAX_GRAPH_LENGTH = 90;

function SafeDrivingContentsKr({vin}: SafeDrivingContentsKrProps) {
  const {t} = useInferableTranslation();
  const {checkGlobalVinWhitelist} = useFirebaseStore(({checkGlobalVinWhitelist}) => ({checkGlobalVinWhitelist}));
  const today = new Date();
  const navigate = useNavigate();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {openAlert, backOrClose} = useCcWebviewUtilsHelper();
  const {checkAvailableComponent} = useAvailableComponentKr({vin});
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  const {
    scoreDate,
    brstAccCount,
    safetyDrvScore,
    nightDrvCount,
    strtBrstAccCnt,
    brstDecCount,
    rangeDrvDist,
    fromAgreeDate,
    daysOfRecentlyTrips,
    predictionScoreFromDistances,
  } = useSafeDriving(vin);
  const {displayInsurancesData, ubiData} = useUbi({vin});
  const {data: dailySafeDrivingData} = useSuspenseQuery({
    queryKey: [
      getQueryKey(diApi.ubiController.getDailyUbi2),
      vin,
      getYYYYMMddFormat(subDate(today, {days: MAX_GRAPH_LENGTH - 1})),
      getYYYYMMddFormat(today),
    ],
    queryFn: () =>
      diApi.ubiController.getDailyUbi2({
        vin,
        startDate: getYYYYMMddFormat(subDate(today, {days: MAX_GRAPH_LENGTH - 1})),
        endDate: getYYYYMMddFormat(today),
      }),
    select: ({data}: ApiResponseGetDailyUbiResponse) => {
      return {
        ...data,
        ubi: data.ubi.map((ubiItem) => {
          return {
            ...ubiItem,
            scoreDate: parseDateWithFormat(ubiItem.scoreDate),
          };
        }),
      };
    },
  });

  const initialSelectedButtonRef = useRef<HTMLButtonElement>(null);
  const predictGraphBarRef = useRef<HTMLSpanElement>(null);
  const [isOpenScoreStandard, setIsOpenScoreStandard] = useState(true);
  const [predictIndex, setPredictIndex] = useState(0);
  const [predictBarWidth, setPredictBarWidth] = useState<string | number>(0);

  async function openNoDataAlert() {
    await openAlert({
      title: t('T20201'),
      message: t('T20202'),
    });
    backOrClose();
  }

  function changeSelectedScore(targetWidth: number, targetOffsetLeft: number, index: number) {
    if (predictGraphBarRef.current) {
      const graphBar = predictGraphBarRef.current;
      const barWidth = index === 3 ? '100%' : Math.round(targetWidth / 2 + targetOffsetLeft - graphBar.offsetLeft + 4);

      setPredictIndex(index);
      setPredictBarWidth(barWidth);
    }
  }

  async function openIrrecoverableBottomSheet() {
    // 40점 이하 or 급가속/급감속 횟수 합산 200회 이상 = 회생불가 고객
    const lastModalTime = localStorage.getItem(IRRECOVERABLE_CLOSE_DUE_DATE);
    if (!lastModalTime) {
      const result = await ccWebviewUtils.openBottomSheetWebViewUrl<boolean>(
        ROUTE_PATHS.BOTTOM_SHEET_SAFE_DRIVING_IRRECOVERABLE.getFullPath(),
        {
          vin,
        }
      );
      if (result) {
        navigate(ROUTE_PATHS.SAFE_DRIVING_RESET.getFullPath(vin));
      }
      return;
    }
    const diffInDays = Math.floor((new Date().getTime() - new Date(lastModalTime).getTime()) / (1000 * 60 * 60 * 24));
    if (diffInDays >= CLOSE_DAYS) {
      const result = await ccWebviewUtils.openBottomSheetWebViewUrl<boolean>(
        ROUTE_PATHS.BOTTOM_SHEET_SAFE_DRIVING_IRRECOVERABLE.getFullPath(),
        {vin}
      );
      if (result) {
        navigate(ROUTE_PATHS.SAFE_DRIVING_RESET.getFullPath(vin));
      }
    }
  }

  useEffect(() => {
    // 0. data 구성 불가 case
    if (safetyDrvScore === 0 && rangeDrvDist === 0) {
      openNoDataAlert();
      return;
    }
    // 1. 회생불가 case
    if (safetyDrvScore <= 40 && brstAccCount + brstDecCount >= 200) {
      openIrrecoverableBottomSheet();
    }

    const target = initialSelectedButtonRef.current;
    if (target) {
      // DEFAULT : 500KM
      changeSelectedScore(target.offsetWidth, target.offsetLeft, 2);
    }
  }, []);

  return (
    <main className="content page_safe_driving">
      <ErrorBoundary fallback={<></>}>
        <Suspense fallback={<ConsentBannerLoadingKr />}>
          <ConsentBannerKr vin={vin} />
        </Suspense>
      </ErrorBoundary>
      <section className="section section_safe_driving">
        <h2 className="blind">{t('T20125')}</h2>
        <div className="my_driving_score">
          <div className="my_score_box">
            <div className="my_score_imgbox">
              <LottieSelector
                id={'safe_driving_score_good_light'}
                kiaLightSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good_light.json')}
                kiaDarkSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good.json')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/json/hyundai/safe_driving_score_good.json')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/json/hyundai/safe_driving_score_good.json')}
                genesisLightSrc={() => import('@di-shared/assets/img/json/genesis/safe_driving_score_good.json')}
                genesisDarkSrc={() => import('@di-shared/assets/img/json/genesis/safe_driving_score_good.json')}
                loop={true}
                autoplay={true}
              />
            </div>
            <div className="my_score_info">
              <div className="my_score_point">
                <span className="my_score_value">{safetyDrvScore}</span>
                <span className="my_score_unit">{t('T10056')}</span>
              </div>
              {safetyDrvScore === 0 && rangeDrvDist === 0 ? (
                <span className="my_score_update">{ERROR.DASH}</span>
              ) : (
                <span className="my_score_update">
                  {t('T10077', {date: scoreDate ? CldrDatetimeFormatter.format(scoreDate, 'yMMMaHM') : ERROR.DASH})}
                </span>
              )}
            </div>
          </div>
          <div className="score_factor">
            <ul className="score_factor_list">
              <li className="score_factor_item">
                <span className="score_factor_title">{t('T10011')}</span>
                <span className="score_factor_value">{t('T10014_$count', {count: brstAccCount})}</span>
              </li>
              <li className="score_factor_item">
                <span className="score_factor_title">{t('T10012')}</span>
                <span className="score_factor_value">{t('T10014_$count', {count: brstDecCount})}</span>
              </li>
              <li className="score_factor_item">
                <span className="score_factor_title">{t('T10013')}</span>
                <span className="score_factor_value">{t('T10014_$count', {count: strtBrstAccCnt})}</span>
              </li>
              <li className="score_factor_item">
                <span className="score_factor_title">{t('T20000')}</span>
                <span className="score_factor_value">{t('T10014_$count', {count: nightDrvCount})}</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="section_container safe_driving_standard">
          <div className="driving_accordion type_container">
            <div className="driving_accordion_item">
              <h3>
                <button
                  type="button"
                  className="driving_accordion_btn"
                  aria-expanded={isOpenScoreStandard}
                  aria-controls="accordion_content01"
                  onClick={() => {
                    emitEventLog({
                      event: 'di_driveinfo_interaction',
                      data_action: 'hide banner',
                      data_name: t('T20126'),
                    });
                    setIsOpenScoreStandard((prev) => !prev);
                  }}
                >
                  <span className="driving_accordion_title">{t('T20126')}</span>
                  <span
                    className="ic ic_arrow_down_16px"
                    aria-hidden="true"
                  ></span>
                </button>
              </h3>
              {isOpenScoreStandard && (
                <div
                  className="driving_accordion_content"
                  id="accordion_content01"
                  style={{display: 'block'}}
                >
                  <ul className="score_standard_list">
                    <li className="score_standard_item">
                      <div className="score_standard_icon">
                        <span className="ic ic_calendar_24px"></span>
                      </div>
                      <div className="score_standard_info">
                        <strong className="score_standard_title">{t('T20127')}</strong>
                        <span
                          className="score_standard_desc"
                          style={{whiteSpace: 'pre-wrap'}}
                        >
                          {t('T20128_$fromDate_$toDate_$totalDays', {
                            fromDate: fromAgreeDate,
                            toDate: scoreDate ? CldrDatetimeFormatter.format(scoreDate, 'long') : ERROR.DASH,
                            totalDays: daysOfRecentlyTrips,
                          })}
                        </span>
                      </div>
                    </li>
                    <li className="score_standard_item">
                      <div className="score_standard_icon">
                        <span
                          className="ic ic_drive_distance_24px"
                          aria-hidden="true"
                        ></span>
                      </div>
                      <div className="score_standard_info">
                        <strong className="score_standard_title">{t('T20129')}</strong>
                        <span
                          className="score_standard_desc"
                          style={{whiteSpace: 'pre-wrap'}}
                        >
                          {t('T20130_$days_$distance', {
                            days: daysOfRecentlyTrips,
                            distance: `${convertDistanceValue(rangeDrvDist)}${convertDistanceSymbol(rangeDrvDist)}`,
                          })}
                        </span>
                      </div>
                    </li>
                  </ul>

                  <div className="driving_banner type_square">
                    <LinkTo
                      to={ROUTE_PATHS.SAFE_DRIVING_GUIDE.getFullPath()}
                      className="driving_banner_box"
                      onClick={() =>
                        emitEventLog({
                          event: 'di_driveinfo_interaction',
                          data_action: 'view banner',
                          data_name: t('T20131'),
                        })
                      }
                    >
                      <span className="driving_banner_txtbox type_reverse">
                        <strong className="driving_banner_title body_medium_b">{t('T20131')}</strong>
                        <span className="driving_banner_desc label_small_b">{t('T20132')}</span>
                      </span>
                      <span className="driving_banner_imgbox">
                        <ImageSelector
                          className="driving_banner_img"
                          alt="driving_banner_img"
                          kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          hyundaiLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          genesisLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          genesisDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                        />
                      </span>
                    </LinkTo>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="section_container safe_driving_daily">
          <div className="section_container_header">
            <h3 className="title_xxsmall">{t('T20009')}</h3>
          </div>
          <ErrorBoundary
            fallback={
              <>
                <p className="section_container_desc">{t('T20187')}</p>
                <DailyLineGraphError />
              </>
            }
          >
            <SafeDrivingDailyGraphKr
              data={[
                ...dailySafeDrivingData.ubi.map((item) => ({
                  score: item.safetyDrvScore,
                  scoreDate: item.scoreDate,
                })),
              ].slice(-MAX_GRAPH_LENGTH)}
            />
          </ErrorBoundary>

          <ul className="statistics_info_list">
            <li className="statistics_info_item">
              <span className="statistics_info_key">{t('T20010')}</span>
              <span className="statistics_info_val">
                <span className="statistics_info_number">{dailySafeDrivingData.minScore}</span>
                <span className="statistics_info_unit">{t('T10056')}</span>
              </span>
            </li>
            <li className="statistics_info_item">
              <span className="statistics_info_key">{t('T20011')}</span>
              <span className="statistics_info_val">
                <span className="statistics_info_number">{dailySafeDrivingData.maxScore}</span>
                <span className="statistics_info_unit">{t('T10056')}</span>
              </span>
            </li>
          </ul>
        </div>
        <SafeDrivingMonthlyScoreTrend vin={vin} />

        {checkExcludeBrand(BrandType.KIA, BrandType.HYUNDAI, BrandType.GENESIS) && checkAvailableComponent(PermissionTypeKr.TRIP_FUEL) && (
          <ErrorBoundary fallback={<></>}>
            <Suspense fallback={<SafeDrivingMainTripLoadingKr />}>
              <SafeDrivingMainTripKr vin={vin} />
            </Suspense>
          </ErrorBoundary>
        )}
        {checkExcludeBrand(BrandType.KIA, BrandType.HYUNDAI, BrandType.GENESIS) && checkAvailableComponent(PermissionTypeKr.TRIP_FUEL) && (
          <ErrorBoundary fallback={<></>}>
            <Suspense fallback={<SafeDrivingNightLoadingKr />}>
              <SafeDrivingNightKr
                vin={vin}
                nightDrvCount={ubiData.nightDrvCount}
              />
            </Suspense>
          </ErrorBoundary>
        )}
        <div className="section_container safe_driving_ubi">
          <div className="section_container_header">
            <h3 className="title_xxsmall">{t('T20139')}</h3>
          </div>
          <p
            className="section_container_desc"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T20140_$score_$discount', {
              score: safetyDrvScore,
              discount: displayInsurancesData[0].totalDiscount,
            })}
          </p>
          <div className="container_banner_imgbox">
            <ImageSelector
              alt="insurance_benefit_iocn"
              kiaLightSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
              kiaDarkSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
              genesisLightSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
              genesisDarkSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
            />
          </div>
          <LinkTo
            to={ROUTE_PATHS.UBI.getFullPath(vin)}
            className="section_container_full_btn"
            onClick={() =>
              emitEventLog({
                event: 'di_driveinfo_interaction',
                data_action: 'view banner',
                data_name: t('T20139'),
              })
            }
          >
            <span className="blind">{t('T20141')}</span>
          </LinkTo>
        </div>
        <div className="section_container safe_driving_predict">
          <div className="section_container_header">
            <h3 className="title_xxsmall">{t('T20142')}</h3>
          </div>
          <p
            className="section_container_desc"
            id="scorePredictDescription"
            role="tabpanel"
            aria-labelledby="scorePredict500"
            aria-live="polite"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T20143_$distance_$score', {
              distance: `${convertDistanceValue(predictionScoreFromDistances[predictIndex].distance)}${convertDistanceSymbol(
                predictionScoreFromDistances[predictIndex].distance
              )}`,
              score: predictionScoreFromDistances[predictIndex].score,
            })}
          </p>
          <div className="score_predict">
            <span className="speech_bubble">
              <span className="blind">{t('T20144')}</span>
              <span className="score_predict_point">{t('T20006_$score', {score: safetyDrvScore})}</span>
              <span className="blind">{t('T20145')}</span>
              <span className="score_predict_distance">
                {convertDistanceValue(rangeDrvDist)}
                {convertDistanceSymbol(rangeDrvDist)}
              </span>
            </span>
            <span
              className="predict_graph_bar"
              ref={predictGraphBarRef}
            >
              <span
                className={clsx('bar_current', {
                  'is-end': predictIndex === predictionScoreFromDistances.length - 1,
                })}
                style={{width: predictBarWidth}}
              >
                <span
                  className="speech_bubble"
                  aria-hidden="true"
                >
                  <span className="score_predict_point">
                    {t('T20006_$score', {score: predictionScoreFromDistances[predictIndex].score})}
                  </span>
                  <span className="score_predict_distance">
                    {convertDistanceValue(rangeDrvDist + predictionScoreFromDistances[predictIndex].distance)}
                    {convertDistanceSymbol(rangeDrvDist + predictionScoreFromDistances[predictIndex].distance)}
                  </span>
                </span>
              </span>
            </span>
            <div
              className="score_predict_control"
              role="tablist"
            >
              <button
                type="button"
                className="score_predict_btn"
                id="scorePredictCurrent"
                role="tab"
                aria-selected="false"
                aria-controls="scorePredictDescription"
                disabled={true}
              >
                {t('T20146')}
              </button>
              <button
                type="button"
                className="score_predict_btn"
                id="scorePredict100"
                role="tab"
                aria-selected={predictIndex === 0}
                aria-controls="scorePredictDescription"
                onClick={(e) => {
                  const target = e.target as HTMLButtonElement;
                  changeSelectedScore(target.offsetWidth, target.offsetLeft, 0);
                }}
              >
                +{convertDistanceValue(100)}
                {convertDistanceSymbol(100)}
              </button>
              <button
                type="button"
                className="score_predict_btn"
                id="scorePredict300"
                role="tab"
                aria-selected={predictIndex === 1}
                aria-controls="scorePredictDescription"
                onClick={(e) => {
                  const target = e.target as HTMLButtonElement;
                  changeSelectedScore(target.offsetWidth, target.offsetLeft, 1);
                }}
              >
                +{convertDistanceValue(300)}
                {convertDistanceSymbol(300)}
              </button>
              <button
                ref={initialSelectedButtonRef}
                type="button"
                className="score_predict_btn"
                id="scorePredict500"
                role="tab"
                aria-selected={predictIndex === 2}
                aria-controls="scorePredictDescription"
                onClick={(e) => {
                  const target = e.target as HTMLButtonElement;
                  changeSelectedScore(target.offsetWidth, target.offsetLeft, 2);
                }}
              >
                +{convertDistanceValue(500)}
                {convertDistanceSymbol(500)}
              </button>
              <button
                type="button"
                className="score_predict_btn"
                id="scorePredict1000"
                role="tab"
                aria-selected={predictIndex === 3}
                aria-controls="scorePredictDescription"
                onClick={(e) => {
                  const target = e.target as HTMLButtonElement;
                  changeSelectedScore(target.offsetWidth, target.offsetLeft, 3);
                }}
              >
                +{convertDistanceValue(1000)}
                {convertDistanceSymbol(1000)}
              </button>
            </div>
          </div>
        </div>
        <SafeDrivingAgreeBannerKr vin={vin} />
      </section>
    </main>
  );
}

export default SafeDrivingContentsKr;
